import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { TrialBalance, GeneralLedgerEntry } from '@/types/accounting';

// =====================================================
// TRIAL BALANCE
// =====================================================

export function useTrialBalance(
  companyId: string,
  fiscalYearId: string,
  filters?: {
    dateFrom?: string;
    dateTo?: string;
  }
) {
  return useQuery({
    queryKey: ['trial-balance', companyId, fiscalYearId, filters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('generate_trial_balance', {
        p_company_id: companyId,
        p_fiscal_year_id: fiscalYearId,
        p_date_from: filters?.dateFrom || null,
        p_date_to: filters?.dateTo || null,
      });

      if (error) throw error;
      return data as TrialBalance[];
    },
    enabled: !!companyId && !!fiscalYearId,
    staleTime: 60000, // 1 minute
  });
}

// =====================================================
// GENERAL LEDGER
// =====================================================

export function useGeneralLedger(
  companyId: string,
  accountId: string,
  filters?: {
    dateFrom?: string;
    dateTo?: string;
  }
) {
  return useQuery({
    queryKey: ['general-ledger', companyId, accountId, filters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('generate_general_ledger', {
        p_company_id: companyId,
        p_account_id: accountId,
        p_date_from: filters?.dateFrom || null,
        p_date_to: filters?.dateTo || null,
      });

      if (error) throw error;
      return data as GeneralLedgerEntry[];
    },
    enabled: !!companyId && !!accountId,
    staleTime: 60000, // 1 minute
  });
}
